import { FunctionComponent, useEffect, useState } from 'react'
import { Role } from '@/types/Role'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import NoDaycare from '../../noDaycare/noDaycare'
import styles from './daycareList.module.scss'
import Title from '@/components/_custom/Title/title'
import DaycaresTable from './DaycaresTable/daycaresTable'
import moment from 'moment-timezone'
import Loading from '@/components/Loading/Loading'
import { Daycare } from '@/types/Daycare'
import ButtonText from '@/components/_custom/Buttons/ButtonText/ButtonText.tsx'

const annuaireUrl: string | undefined = import.meta.env.VITE_ANNUAIRE_URL

const DaycareList: FunctionComponent = () => {
    const { t } = useTranslation()

    const { user, fetchDaycares } = useAuth()

    const [daycaresList, setDaycaresList] = useState<Array<Daycare>>([])

    const [loading, setLoading] = useState<boolean>(true)

    useEffect(() => {
        setLoading(true)
        setDaycaresList([])
        try {
            setDaycaresList(user?.daycares || [])
        } finally {
            setLoading(false)
        }
    }, [user?.daycares])

    return loading ? (
        <Loading overall />
    ) : (
        <>
            {user && user.role ? (
                daycaresList.length === 0 ? (
                    <NoDaycare role={user.role} />
                ) : user.role == Role.parent || user.role === Role.staff ? (
                    <Navigate to={'/daycare?daycare=' + daycaresList[0].id} replace />
                ) : (
                    <div className={styles.PagePosition}>
                        <div className={styles.DataGridContainer}>
                            <div className={styles.Title}>
                                <Title text={t('myDaycares')} />
                            </div>
                            <DaycaresTable
                                daycares={
                                    daycaresList.length >= 1
                                        ? [...daycaresList]
                                              .map((daycare: Daycare) => ({
                                                  ...daycare,
                                                  formattedValidityEnd: daycare.validityEnd
                                                      ? moment(daycare.validityEnd, 'DD-MM-YYYY').format('YYYY-MM-DD')
                                                      : ''
                                              }))
                                              .sort((a: Daycare, b: Daycare) => a.city.localeCompare(b.city))
                                        : []
                                }
                                role={user.role}
                                refreshDaycares={fetchDaycares}
                            />
                            {user.role === Role.cald && (
                                <div className={styles.FooterContainer}>
                                    <ButtonText
                                        text={t('showOnAnnuaire')}
                                        onClick={() => window.open(generateAnnuaireUrl(daycaresList), '_blank')}
                                        className={styles.UrlAnnuaire}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )
            ) : (
                <Navigate to={'/'} />
            )}
        </>
    )
}

export default DaycareList

function generateAnnuaireUrl(daycares: Daycare[]): string {
    const annuaireIds: string = daycares
        .filter((d: Daycare): boolean => d.annuaireId !== null && d.annuaireId !== undefined && d.active === true)
        .map((d: Daycare): string => d.annuaireId as string)
        .join(',')

    return `${annuaireUrl}/admin/daycares/list?ids=${annuaireIds}`
}
